import { z } from 'zod'
import { auditFieldsSchema, uuidSchema } from './common.ts'
import { grossUnitPriceSchema } from './deal-line-items.ts'
import { unitSchema } from './units.ts'

export const CATALOG_ITEM_KINDS = ['product', 'service'] as const

export const catalogItemKindSchema = z.enum(CATALOG_ITEM_KINDS)
export type CatalogItemKind = z.infer<typeof catalogItemKindSchema>

export const catalogItemSchema = z
  .object({
    id: uuidSchema,
    kind: catalogItemKindSchema,
    name: z.string().trim().min(1).max(200),
    /** The shop's short product code («СП-2»). Optional; unique ignoring case. */
    code: z.string().trim().min(1).max(40).nullable(),
    unit: unitSchema,
    grossUnitPrice: grossUnitPriceSchema,
    engineeringProductId: uuidSchema.nullable(),
    engineeringProductName: z.string().nullable(),
    isActive: z.boolean(),
  })
  .extend(auditFieldsSchema.shape)

export type CatalogItem = z.infer<typeof catalogItemSchema>

const catalogItemInputSchema = catalogItemSchema.pick({
  kind: true,
  name: true,
  code: true,
  unit: true,
  grossUnitPrice: true,
  engineeringProductId: true,
})

export const createCatalogItemInputSchema = catalogItemInputSchema.extend({
  code: catalogItemSchema.shape.code.optional(),
  engineeringProductId: uuidSchema.nullable().default(null),
})

export type CreateCatalogItemInput = z.infer<typeof createCatalogItemInputSchema>

export const updateCatalogItemInputSchema = catalogItemInputSchema
  .partial()
  .extend({ isActive: z.boolean().optional() })
  .refine((input) => Object.keys(input).length > 0)

export type UpdateCatalogItemInput = z.infer<typeof updateCatalogItemInputSchema>

/**
 * What an engineer may change on a product (client decision, 13 Oct): its name, code and price, and
 * the technology card it is made by. Kind, unit and archiving stay with the admin.
 */
export const updateCatalogProductInputSchema = catalogItemSchema
  .pick({ name: true, code: true, grossUnitPrice: true, engineeringProductId: true })
  .partial()
  .refine((input) => Object.keys(input).length > 0)

export type UpdateCatalogProductInput = z.infer<typeof updateCatalogProductInputSchema>
